import { MenuItem } from "@material-ui/core";
import { isNil } from "lodash";
import moment from "moment";
import React, { ReactComponentElement, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import DataTable from "../../../../components/DataTable/DataTable";
import TruncatedText, { TruncatedTextProps } from "../../../../components/TruncatedText/TruncatedText";
import { IDataTableRow } from "../../../../components/DataTable/DataTableBody/DataTableBody";
import {
  DataTableContextFilters,
  useDataTableContext,
} from "../../../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FilterType } from "../../../../components/DataTable/DataTableFilters/DataTableFilters";
import { Column } from "../../../../components/DataTable/DataTableHead/DataTableHead";
import DelegeeStatus, { Statuses } from "../../../../components/DelegeeStatus/DelegeeStatus";
import { FmpFilters } from "../../../../constants/filtering.constants";
import useAppContext from "../../../../context/hooks/useAppContext";
import useFleetManagerContext from "../../../../context/hooks/useFleetManagerContext";
import useMyProductsContext from "../../../../context/hooks/useMyProductsContext";
import { useOnBulkImportFinishedCallback } from "../../../../hooks/useOnBulkImportFinishedCallback";
import { ForFleetManager } from "../../../../models/ForFleetManager";
import { ContractType, ParkingProductType } from "../../../../models/ParkingProductModel";
import { DelegatedEntryRight } from "../../../../models/delegations/DelegatedEntryRight";
import { WithFiltersAndPaging } from "../../../../models/filters/WithFiltersAndPaging";
import * as entryRightsService from "../../../../services/entryRights.service";
import { useAppDispatch, useAppSelector } from "../../../../store";
import {
  getDelegatedEntryRightsThunk,
  resendInviteActionThunk,
  toggleShowDeleteEntryRightsDelegeesAction,
  toggleShowRevokeEntryRightsAction
} from "../../../../store/actions/entryRights.actions";
import * as parkingProductsThunks from "../../../../store/actions/parkingProducts.thunks";
import { ForbiddenIconMenu } from "../../../../ui/Icons/ForbiddenIcon";
import {
  DeleteDelegeeIcon,
  DeleteDelegeeMenuItem,
  EditIcon,
  NoPaddingGrid,
  ResendInvitationIcon,
} from "../../FleetManagerDataTable/StyledComponents";
import filteredColumnObjectBuilder from "../ObjectBuilders/FilteredColumnObjectBuilder";
import {
  EntryRightProductKey as EntryRightMyProductsRowKey,
  ProductChip,
  selectEnableDelegationsScheduling,
  useCurrentlySelectedParkingProduct,
  useGetOccupiedParkingRightsCount,
  useProductOptions,
  useSharedProductsMenuAction,
} from "./common";

const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;

export interface DelegatedEntryRightRow extends IDataTableRow {
  rowKey: EntryRightMyProductsRowKey
  name: JSX.Element;
  email: React.ReactElement<TruncatedTextProps>;
  product: ReactNode;
  invitationDate: string;
  activationDate?: string | null;
  expirationDate?: string | null;
  status: ReactComponentElement<typeof DelegeeStatus>;
}

export type EntryRightTableProps = Readonly<{
  onRevokeClicked: () => void;
  onDeleteClicked: () => void;
  subHeading?: ReactNode;
  parkingProductType: ParkingProductType;
  translationKey: "parkingRights" | "pedestrianRights";
}>

function createData(
    delegatedEntryRight: DelegatedEntryRight,
    enableDelegationsScheduling?: boolean
): DelegatedEntryRightRow {
    const activationDate = delegatedEntryRight.activationDate
        ? moment.parseZone(delegatedEntryRight.activationDate).format("DD/MM/YYYY")
        : '';
    const expirationDate = delegatedEntryRight.expirationDate
        ? moment.parseZone(delegatedEntryRight.expirationDate).format("DD/MM/YYYY")
        : null;

    return {
        rowKey: {
            entryRightId: delegatedEntryRight.id,
            delegeeId: delegatedEntryRight.delegee.id,
            pmcId: delegatedEntryRight.product.pmcId,
        },
        name: <TruncatedText text={delegatedEntryRight.delegee.name} />,
        email: <TruncatedText text={delegatedEntryRight.delegee.email} />,
        product: (
            <Wrapper>
                <ProductChip
                    name={delegatedEntryRight.product.pmcName}
                    location={delegatedEntryRight.product.locationName}
                />
            </Wrapper>
        ),
        invitationDate: moment(delegatedEntryRight.invitationDate).format("DD/MM/YYYY"),
        ...(enableDelegationsScheduling ? { activationDate, expirationDate } : {}),
        status: <DelegeeStatus type={delegatedEntryRight.status} />,
    };
}

type MenuItemsProps = Readonly<{
  onDelete: () => void;
  onEdit: () => void;
  onRevoke: () => void;
  onResend: () => void;
  resendDisabled?: boolean;
}>;

function MenuItems(props: MenuItemsProps) {
  const { t } = useTranslation("fleetManagerTable");

  return (
    <>
      <MenuItem key="edit" onClick={() => props.onEdit()}>
        <EditIcon />
        {t("contextMenuActions.editDelegee")}
      </MenuItem>
      <MenuItem key="resend" onClick={() => props.onResend()} disabled={props.resendDisabled}>
        <ResendInvitationIcon />
        {t("contextMenuActions.resendInvite")}
      </MenuItem>
      <MenuItem key="revoke" onClick={() => props.onRevoke()}>
        <ForbiddenIconMenu />
        {t("contextMenuActions.revokeDelegee")}
      </MenuItem>
      <DeleteDelegeeMenuItem key="delete" onClick={() => props.onDelete()}>
        <DeleteDelegeeIcon />
        {t("contextMenuActions.deleteDelegee")}
      </DeleteDelegeeMenuItem>
    </>
  );
}

function EntryRightTable(props: EntryRightTableProps) {
  const dispatch = useAppDispatch();
  const delegatedEntryRights = useAppSelector((state) => {
    switch (props.parkingProductType) {
      case ParkingProductType.ParkingRights:
        return state.entryRights.myProducts.parking;
      case ParkingProductType.PedestrianAccess:
        return state.entryRights.myProducts.pedestrian;
    }
  });
  const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);
  const selectedLocatedProduct = useCurrentlySelectedParkingProduct();

  const getDelegatedEntryRights = useCallback(
    (request: WithFiltersAndPaging<ForFleetManager>) => {
      const contractProductTypes: entryRightsService.ContractProductTypes =
        selectedLocatedProduct?.contractType === ContractType.GoBusiness
          ? { contractType: ContractType.GoBusiness, productType: props.parkingProductType }
          : { contractType: ContractType.SeasonTicket, productType: props.parkingProductType };
      const query = entryRightsService.convertFiltersToGetDelegatedEntryRightsQuery(request, contractProductTypes);
      return dispatch(getDelegatedEntryRightsThunk(query));
    },
    [dispatch, props.parkingProductType, selectedLocatedProduct?.contractType]
  );
  const { t } = useTranslation(["myProducts", "globals", "dataTable", "fleetManagerTable"]);

  const { myProductsState, setSorting } = useMyProductsContext();
  const {
    fleetManagerState,
    setSelectedDelegeeId,
    setSelectedParkingRightIds,
    togglePedestrianEditForm
  } = useFleetManagerContext();

  const dataTableContext = useDataTableContext();

  const { appState } = useAppContext();

  const getTotalOccupiedParkingRights = useGetOccupiedParkingRightsCount();

  const toggleRevokePedestrianWarning = () => dispatch(toggleShowRevokeEntryRightsAction());
  const toggleDeleteEntryRightDelegeesWarning = () => dispatch(toggleShowDeleteEntryRightsDelegeesAction());
  const sortBy = myProductsState.dataTable.sortBy || "invitationDate";

  const getFilters = (): DataTableContextFilters[] => {
    const filters = [
      {
        key: FmpFilters.placeId,
        type: FilterType.QueryParam,
        value: myProductsState.selectedLocation.placeId.toString(),
      },
      {
        key: FmpFilters.sortBy,
        type: FilterType.QueryParam,
        value: sortBy,
      },
      {
        key: FmpFilters.orderDesc,
        type: FilterType.QueryParam,
        value: myProductsState.dataTable.orderDesc?.toString(),
      },
      ...(dataTableContext.state.filters as DataTableContextFilters[]),
    ];

    return filters;
  };

  const getRequestModel = (): WithFiltersAndPaging<ForFleetManager> => {
    const request: WithFiltersAndPaging<ForFleetManager> = {
      entity: {
        seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
        language: appState.selectedLanguage,
      },
      pagination: {
        pageSize: dataTableContext.state.pageSize!,
        pageNumber: dataTableContext.state.pageNumber!,
      },
      filters: getFilters(),
    };

    return request;
  };

  const [fetchKey, setFetchKey] = useState(0);

  const fetchDelegatedEntryRights = () => {
    if (appState.user.seasonTicketOwnerCrmId) {
      return getDelegatedEntryRights(getRequestModel());
    }
  };

  const fetchLocatedParkingProducts = () => {
    if (appState.user.seasonTicketOwnerCrmId) {
      dispatch(parkingProductsThunks.getLocatedParkingProductsThunk(
        {seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId,
          language: appState.selectedLanguage }));
    }
  };

  const refetchDelegations = () => {
    fetchDelegatedEntryRights();
    fetchLocatedParkingProducts();
  };

  useOnBulkImportFinishedCallback(refetchDelegations);

  const memoizedFetch = useCallback(
    () => fetchDelegatedEntryRights(), // eslint-disable-next-line
    [
      dataTableContext.state.pageSize,
      dataTableContext.state.pageNumber,
      myProductsState.dataTable.orderDesc,
      sortBy,
      myProductsState.selectedLocation.placeId,
      appState.user.seasonTicketOwnerCrmId,
      appState.selectedLanguage,
      fetchKey
    ]
  );

  useEffect(() => {
    const res = memoizedFetch();
    return () => res?.abort();
  }, [memoizedFetch]);

  const goBusinessOptions = useProductOptions({ productType: ParkingProductType.ParkingRights, contractType: ContractType.GoBusiness });
  const seasonTicketOptions = useProductOptions({ productType: props.parkingProductType, contractType: ContractType.SeasonTicket });
  const productTableOptions = selectedLocatedProduct?.contractType === ContractType.GoBusiness
    ? goBusinessOptions
    : seasonTicketOptions;

  const sharedProductActions = useSharedProductsMenuAction();

  const columns: Column<DelegatedEntryRightRow>[] = filteredColumnObjectBuilder.buildDelegatedEntryRightsColumns(
    t,
    productTableOptions,
    enableDelegationsScheduling,
  );

  const onSort = (event: React.MouseEvent<unknown>, property: keyof DelegatedEntryRightRow) => {
    setSorting(property.toString());
  };

  const getTableRows = () => {
    const { pages } = delegatedEntryRights;
    if (isNil(pages)) return [];
    const pageData = pages[dataTableContext.state.pageNumber!]?.data;
    if (isNil(pageData)) return [];
    return pageData.map(x => createData(x, enableDelegationsScheduling));
  };

  const isLoading =
    delegatedEntryRights.pages?.[dataTableContext.state.pageNumber!]?.loading ?? false;
  const rows = getTableRows();

  const handleEdit = (row: DelegatedEntryRightRow | null) => {
    if (isNil(row)) {
      return;
    }
    togglePedestrianEditForm(row.rowKey.delegeeId || "");
  };

  const handleDelete = (row: DelegatedEntryRightRow | null) => {
    if (isNil(row)) {
      return;
    }
    const registrationId = row.rowKey.delegeeId || "";
    setSelectedDelegeeId(registrationId);
    toggleDeleteEntryRightDelegeesWarning();
  };

  const handleRevoke = (row: DelegatedEntryRightRow | null) => {
    if (isNil(row)) {
      return;
    }
    setSelectedDelegeeId(row.rowKey as any);
    toggleRevokePedestrianWarning();
  };

  const handleResend = (row: DelegatedEntryRightRow | null, onClose: () => void) => {
    if (isNil(row)) {
      return;
    }

    dispatch(resendInviteActionThunk({ email: row.email.props.text || "", customerId: appState.user.seasonTicketOwnerCrmId!, entryRightId: row.rowKey.entryRightId }));
    onClose();
  }

  return (
    <NoPaddingGrid container item direction="row" xs={12}>
      <NoPaddingGrid container item direction="column" xs={12}>
        <DataTable
          rows={rows}
          columns={columns}
          key={myProductsState.selectedLocation.placeId}
          loading={isLoading}
          orderBy={sortBy}
          pages={delegatedEntryRights.totalPages}
          onFilterClicked={() => setFetchKey(prev => prev + 1)}
          onResetFiltersClicked={() => setFetchKey(prev => prev + 1)}
          onRequestSort={onSort}
          translationOverrideKey={props.translationKey}
          selectedRowKeys={fleetManagerState.selectedRowKeys}
          setSelectedRowKeys={setSelectedParkingRightIds}
          renderActionMenu={(focusedRow, onClose) => (
            <MenuItems
              onDelete={() => handleDelete(focusedRow)}
              onEdit={() => handleEdit(focusedRow)}
              onRevoke={() => handleRevoke(focusedRow)}
              onResend={() => handleResend(focusedRow, onClose)}
              resendDisabled={focusedRow?.status?.props.type !== Statuses.Pending}
            />
          )}
          order={myProductsState.dataTable.orderDesc ? "desc" : "asc"}
          heading={{
            options: {
              useHeading: true,
              showHeadingCount: true,
            },
            title: t(`globals:${props.translationKey}`),
            count: getTotalOccupiedParkingRights(selectedLocatedProduct || {}),
          }}
          filtering={{
            options: {
              useFilters: true,
            },
          }}
          actions={{
            options: {
              useActions: true,
            },
            revoke: {
              useRevoke: true,
              showRevoke: fleetManagerState.selectedRowKeys.length > 0,
              revokeLabel: `${t("globals:revoke")} (${fleetManagerState.selectedRowKeys.length})`,
              onRevokeClicked: props.onRevokeClicked,
            },
            menu: sharedProductActions.menu,
          }}
          subHeading={props.subHeading}
        />
      </NoPaddingGrid>
    </NoPaddingGrid>
  );
}

export default EntryRightTable;
